import { useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import { findRoute } from '../services/routeFinder'
import { formatFare } from '../services/fareCalculator'
import { FareResult } from '../types/metro.types'
import { BannerAd, AD_SLOTS } from '../components/ads'

function FareCalculator() {
  const { stations, lines, fares, graph, selectedCity, currentCity, isLoading } = useMetro()
  const [source, setSource] = useState('')
  const [destination, setDestination] = useState('')
  const [sourceQuery, setSourceQuery] = useState('')
  const [destQuery, setDestQuery] = useState('')
  const [useSmartCard, setUseSmartCard] = useState(false)
  const [fareResult, setFareResult] = useState<FareResult | null>(null)
  const [showSourceDropdown, setShowSourceDropdown] = useState(false)
  const [showDestDropdown, setShowDestDropdown] = useState(false)

  const sourceRef = useRef<HTMLDivElement>(null)
  const destRef = useRef<HTMLDivElement>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sourceRef.current && !sourceRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false)
      }
      if (destRef.current && !destRef.current.contains(event.target as Node)) {
        setShowDestDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredSourceStations = useMemo(() => {
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name))
    if (!sourceQuery.trim()) {
      return sorted
    }
    return sorted.filter(s => s.name.toLowerCase().includes(sourceQuery.toLowerCase()))
  }, [stations, sourceQuery])

  const filteredDestStations = useMemo(() => {
    const sorted = [...stations].sort((a, b) => a.name.localeCompare(b.name))
    if (!destQuery.trim()) {
      return sorted
    }
    return sorted.filter(s => s.name.toLowerCase().includes(destQuery.toLowerCase()))
  }, [stations, destQuery])

  const getStationLineColors = (stationLines: string[]) => {
    return stationLines.map(lineId => {
      const line = lines.find(l => l.id === lineId)
      return line?.colorHex || '#999'
    })
  }

  const handleCalculateFare = () => {
    if (!source || !destination || !graph || !fares) return

    const result = findRoute(
      graph,
      source,
      destination,
      stations,
      lines,
      fares,
      { preference: 'shortest' }
    )

    if (result) {
      setFareResult(result.fare)
    }
  }

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">No City Selected</h2>
            <p className="text-neutral-dark/70 mb-4">Please select a city to calculate fares</p>
            <Link to="/cities" className="btn-primary">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading metro data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive max-w-4xl">
        <h1 className="text-3xl lg:text-4xl 2xl:text-tv-2xl font-bold text-center mb-2">
          Fare Calculator
        </h1>
        <p className="text-center text-neutral-dark/70 mb-8">
          {currentCity?.metroName} - Calculate fare with smart card discount
        </p>

        {/* Search Form */}
        <div className="card mb-8">
          <div className="space-y-4">
            {/* Source Station */}
            <div className="relative" ref={sourceRef}>
              <label className="block text-sm font-medium mb-2">From Station</label>
              <input
                type="text"
                placeholder="Click to select or type to search..."
                value={sourceQuery}
                onChange={(e) => {
                  setSourceQuery(e.target.value)
                  setShowSourceDropdown(true)
                  setSource('')
                }}
                onFocus={() => setShowSourceDropdown(true)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              />
              {showSourceDropdown && filteredSourceStations.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b text-sm text-neutral-dark/70">
                    {sourceQuery ? `${filteredSourceStations.length} stations found` : `All ${stations.length} stations`}
                  </div>
                  {filteredSourceStations.map(station => {
                    const lineColors = getStationLineColors(station.lines)
                    return (
                      <button
                        key={station.id}
                        className="w-full text-left px-4 py-3 hover:bg-metro-blue-soft flex items-center gap-3 border-b border-gray-50"
                        onClick={() => {
                          setSource(station.id)
                          setSourceQuery(station.name)
                          setShowSourceDropdown(false)
                        }}
                      >
                        <div className="flex gap-0.5 flex-shrink-0">
                          {lineColors.slice(0, 3).map((color, i) => (
                            <div
                              key={i}
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">{station.name}</span>
                          {station.isInterchange && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-metro-violet-soft text-metro-violet rounded">
                              Interchange
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Destination Station */}
            <div className="relative" ref={destRef}>
              <label className="block text-sm font-medium mb-2">To Station</label>
              <input
                type="text"
                placeholder="Click to select or type to search..."
                value={destQuery}
                onChange={(e) => {
                  setDestQuery(e.target.value)
                  setShowDestDropdown(true)
                  setDestination('')
                }}
                onFocus={() => setShowDestDropdown(true)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-metro-blue focus:border-transparent"
              />
              {showDestDropdown && filteredDestStations.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b text-sm text-neutral-dark/70">
                    {destQuery ? `${filteredDestStations.length} stations found` : `All ${stations.length} stations`}
                  </div>
                  {filteredDestStations.map(station => {
                    const lineColors = getStationLineColors(station.lines)
                    return (
                      <button
                        key={station.id}
                        className="w-full text-left px-4 py-3 hover:bg-metro-blue-soft flex items-center gap-3 border-b border-gray-50"
                        onClick={() => {
                          setDestination(station.id)
                          setDestQuery(station.name)
                          setShowDestDropdown(false)
                        }}
                      >
                        <div className="flex gap-0.5 flex-shrink-0">
                          {lineColors.slice(0, 3).map((color, i) => (
                            <div
                              key={i}
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium">{station.name}</span>
                          {station.isInterchange && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-metro-violet-soft text-metro-violet rounded">
                              Interchange
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Smart Card Toggle */}
            <div className="flex items-center justify-between p-4 bg-metro-blue-soft rounded-lg">
              <div>
                <span className="font-medium">Use Smart Card</span>
                <p className="text-sm text-neutral-dark/70">Get up to 10% discount</p>
              </div>
              <button
                onClick={() => setUseSmartCard(!useSmartCard)}
                className={`relative w-14 h-8 rounded-full transition-colors ${
                  useSmartCard ? 'bg-metro-blue' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform ${
                    useSmartCard ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <button
              onClick={handleCalculateFare}
              disabled={!source || !destination}
              className="w-full btn-primary mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Calculate Fare
            </button>
          </div>
        </div>

        {/* Fare Result */}
        {fareResult ? (
          <div className="card">
            <div className="text-center mb-6">
              <div className="text-5xl font-bold mb-2" style={{ color: useSmartCard ? '#81C784' : '#4A90D9' }}>
                {formatFare(useSmartCard ? fareResult.smartCardFare : fareResult.tokenFare)}
              </div>
              <div className="text-neutral-dark/70">
                {useSmartCard ? 'Smart Card Fare' : 'Token Fare'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-metro-blue-soft rounded-lg text-center">
                <div className="text-2xl font-bold text-metro-blue">{formatFare(fareResult.tokenFare)}</div>
                <div className="text-sm text-neutral-dark/70">Token Fare</div>
              </div>
              <div className="p-4 bg-metro-green-soft rounded-lg text-center">
                <div className="text-2xl font-bold text-metro-green">{formatFare(fareResult.smartCardFare)}</div>
                <div className="text-sm text-neutral-dark/70">Smart Card Fare</div>
              </div>
            </div>

            {fareResult.savings > 0 && (
              <div className="p-4 bg-metro-yellow-soft rounded-lg text-center">
                <span className="text-lg">💰 Save </span>
                <span className="text-xl font-bold text-metro-green">{formatFare(fareResult.savings)}</span>
                <span className="text-lg"> with Smart Card!</span>
              </div>
            )}

            <div className="mt-4 text-center text-sm text-neutral-dark/70">
              Distance: {fareResult.distanceKm} km
            </div>
          </div>
        ) : (
          <div className="card text-center py-12 text-neutral-dark/50">
            <span className="text-5xl mb-4 block">💰</span>
            <p>Enter stations to calculate fare</p>
          </div>
        )}

        {/* Banner Ad - After Fare Display */}
        {fareResult && (
          <BannerAd
            slot={AD_SLOTS.FARE_DISPLAY}
            format="rectangle"
            wrapperClassName="mt-8 flex justify-center"
          />
        )}
      </div>
    </div>
  )
}

export default FareCalculator
